import axios from "axios";
import {AxiosRequestConfig} from "axios/index";
import {useCallback, useContext, useMemo} from "react";
import {LoginContext} from "../contexts/LoginContext";
import config from '../utils/config';
import {useNavigate} from "react-router-dom";
import {User} from "../Models/User";
import {USER_STORAGE_KEY} from "../utils/constant";

// Erreur personnalisée pour inclure le code métier du backend
export class ApiError extends Error {
    code: string;
    constructor(message: string, code: string = 'UNKNOWN_ERROR') {
        super(message);
        this.code = code;
        this.name = 'ApiError';
    }
}

// Variables GLOBALES - partagées par tous les composants utilisant useAxios
// Cela évite les refresh concurrents quand plusieurs composants reçoivent 401 simultanément
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void,
    reject: (error: Error) => void
}> = [];


export default function useAxios() {
    const {currentUser, setCurrentUser} = useContext(LoginContext)
    const navigate = useNavigate()

    const headers = useMemo(
        ()=>({Authorization: `Bearer ${currentUser?.tokens?.accessToken}`}),
        [currentUser]
    )

    const processQueue = useCallback((error: any, token: string | null = null) => {
        failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error)
            } else if (token) {
                prom.resolve(token)
            }
        })
        failedQueue = []
    }, [])

    const refreshToken = useCallback(async () => {
        if (!currentUser?.tokens?.refreshToken) {
            throw new Error('No refresh token available')
        }

        try {
            const response = await axios.post(
                `${config.api.baseUrl}/${config.api.version}/auth/refresh-token`,
                { refreshToken: currentUser.tokens.refreshToken }
            )

            const { accessToken, refreshToken: newRefreshToken } = response.data

            // Mise à jour de l'utilisateur avec les nouveaux tokens (rotation)
            const updatedUser = new User({
                uuid: currentUser.uuid,
                email: currentUser.email,
                firstname: currentUser.firstname,
                lastname: currentUser.lastname,
                role: currentUser.role || 'USER',
                isActivated: currentUser.isActivated,
                projects: currentUser.projects,
                defaultProject: currentUser.defaultProject,
                UserProject: currentUser.UserProject,
                tokens: {
                    accessToken,
                    refreshToken: newRefreshToken
                }
            })

            localStorage.setItem(USER_STORAGE_KEY, updatedUser.toJsonString())
            setCurrentUser(updatedUser)

            return accessToken
        } catch (error) {
            // Échec du refresh → déconnexion
            localStorage.removeItem(USER_STORAGE_KEY)
            setCurrentUser(undefined)
            // Seulement si l'utilisateur ÉTAIT connecté (avait un refresh token)
            if (currentUser?.tokens?.refreshToken) {
                sessionStorage.setItem('sessionExpired', 'true')
            }
            navigate("/")
            throw error
        }
    }, [currentUser, setCurrentUser, navigate])

    const _call = useCallback(async (axiosConfig: AxiosRequestConfig) => {
        axiosConfig.baseURL = `${config.api.baseUrl}/${config.api.version}`
        return axios.request(axiosConfig)
            .then((res) => {
                if (res) return res.data
                return null
            }).catch(async (err) => {
                const originalRequest = axiosConfig

                // Si erreur 401 et pas déjà tenté de refresh
                if (err.response && err.response.status === 401 && !(originalRequest as any)._retry) {

                    // Si pas de token existant (première connexion), ne pas tenter de refresh
                    // Extraire et renvoyer le message et code d'erreur du backend
                    if (!currentUser?.tokens?.refreshToken) {
                        const errorMessage = err.response.data?.error?.message
                            || err.response.data?.message
                            || err.response.statusText
                            || 'Authentification échouée';
                        const errorCode = err.response.data?.error?.code || 'UNAUTHORIZED';
                        throw new ApiError(errorMessage, errorCode);
                    }

                    // Si on est déjà en train de refresh, mettre en queue
                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject })
                        }).then(token => {
                            originalRequest.headers = {
                                ...originalRequest.headers,
                                Authorization: `Bearer ${token}`
                            }
                            return axios.request(originalRequest)
                        }).catch(err => {
                            return Promise.reject(err)
                        })
                    }

                    (originalRequest as any)._retry = true
                    isRefreshing = true

                    try {
                        const newAccessToken = await refreshToken()
                        isRefreshing = false
                        processQueue(null, newAccessToken)

                        // Retry la requête originale avec le nouveau token
                        originalRequest.headers = {
                            ...originalRequest.headers,
                            Authorization: `Bearer ${newAccessToken}`
                        }
                        return axios.request(originalRequest)
                    } catch (refreshError) {
                        isRefreshing = false
                        processQueue(refreshError, null)
                        return Promise.reject(refreshError)
                    }
                }

                // Gestion des autres erreurs
                if (err.response && [404,400,403].includes(err.response.status)) {
                    const errorMessage = err.response.data?.error?.message
                        || err.response.data?.message
                        || err.response.statusText
                        || `Erreur ${err.response.status}`;
                    const errorCode = err.response.data?.error?.code || `HTTP_${err.response.status}`;
                    throw new ApiError(errorMessage, errorCode);
                }
                if (err.response) {
                    const errorMessage = err.response.data?.error?.message
                        || err.response.data?.message
                        || err.response.statusText
                        || 'Une erreur est survenue';
                    const errorCode = err.response.data?.error?.code || `HTTP_${err.response.status}`;
                    throw new ApiError(errorMessage, errorCode);
                }
                throw err
            });
    }, [refreshToken, processQueue])

    const postWithAxios = useCallback(async (url: string, data: object,) => {
        return _call({method: "POST", url, data, headers})
    }, [_call, headers])

    const putWithAxios = useCallback(async (url: string, data: object,) => {
        return _call({method: "PUT", url, data, headers})
    }, [_call, headers])

    const getWithAxios = useCallback(async (url: string,) => {
        return _call({method: "GET", url, headers})
    }, [_call, headers])

    const deleteWithAxios = useCallback(async (url: string,) => {
        return _call({method: "DELETE", url, headers})
    }, [_call, headers])

    return {getWithAxios, postWithAxios, putWithAxios, deleteWithAxios}

}